import React, { useState, useEffect, useMemo } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectHasPermission } from "../store/slices/authSlice";
import { Modal, ModalBody } from "reactstrap";
import MainLayout from "../components/layout/MainLayout";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../components/common/Breadcrumbs";
import {
    fetchCases,
    fetchCaseHistory,
    approveOrRejectCase,
} from "../store/slices/casesSlice";

const CasesList = () => {
    const dispatch = useDispatch();
    const { cases, caseHistory, isLoading } = useSelector((state) => state.cases);
    const navigate = useNavigate();
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCase, setSelectedCase] = useState(null);
    const [approvalModal, setApprovalModal] = useState(false);
    const [historyModal, setHistoryModal] = useState(false);
    const [activeSlide, setActiveSlide] = useState(0);
    const [activeAttachmentsRow, setActiveAttachmentsRow] = useState(null);

    const canManageApproval = useSelector((state) =>
        selectHasPermission(state, "case-profile-manage-approval")
    );
    const canSeeCaseHistory = useSelector((state) =>
        selectHasPermission(state, "case-history")
    );
    const canManageProfile = useSelector((state) =>
        selectHasPermission(state, "case-profile-manage")
    );

    // Per-phase form data
    const [phaseData, setPhaseData] = useState(
        Array(7)
            .fill(null)
            .map(() => ({ remark: "", file: null, fileName: "" }))
    );

    /* --------------------------------------------------------------------- */
    /* ---------------------------- FETCH CASES ---------------------------- */
    /* --------------------------------------------------------------------- */
    useEffect(() => {
        dispatch(fetchCases());
    }, [dispatch]);

    /* --------------------------------------------------------------------- */
    /* --------------------------- FILTER & PAGINATION ---------------------- */
    /* --------------------------------------------------------------------- */
    const filteredRecords = useMemo(
        () =>
            cases.filter((r) =>
                Object.values(r).some((v) =>
                    String(v ?? "").toLowerCase().includes(filterText.toLowerCase())
                )
            ),
        [filterText, cases]
    );

    // Get paginated data
    const paginatedRecords = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        return filteredRecords.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredRecords, currentPage, rowsPerPage]);

    const totalRows = filteredRecords.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    /* --------------------------------------------------------------------- */
    /* --------------------------- CUSTOM PAGINATION ----------------------- */
    /* --------------------------------------------------------------------- */
    const CustomPagination = () => {
        const totalPages = Math.ceil(totalRows / rowsPerPage);

        const handlePageChange = (page) => {
            setCurrentPage(page);
        };

        const handleRowsPerPageChange = (newRowsPerPage) => {
            setRowsPerPage(newRowsPerPage);
            setCurrentPage(1); // Reset to first page when changing rows per page
        };

        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                        Rows per page:
                    </span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px" }}
                        value={rowsPerPage}
                        onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                    >
                        {[5, 10, 15, 20, 25, 50].map((v) => (
                            <option key={v} value={v}>
                                {v}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="btn btn-sm border"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages === 0}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    /* --------------------------------------------------------------------- */
    /* ------------------------------ EXPORT ------------------------------- */
    /* --------------------------------------------------------------------- */
    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Cases List", 14, 10);
        const tableColumn = [
            "#",
            "Case ID",
            "Patient",
            "Phone",
            "Program",
            "Created By",
            "Date",
            "Status",
        ];
        const tableRows = filteredRecords.map((r, i) => [
            i + 1,
            r.case_code,
            r.patient_name,
            r.patient_phone,
            r.program.program_name,
            r.created_by,
            r.created_at,
            r.status,
        ]);
        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
        doc.save("cases.pdf");
    };

    const handleCopy = () => {
        const copyText = filteredRecords
            .map(
                (r, i) =>
                    `${i + 1}\t${r.case_number}\t${r.patient_name}\t${r.patient_phone || ""}\t${r.program_name || ""}\t${r.created_by || ""}\t${r.created_at}\t${r.status?.name || ""}`
            )
            .join("\n");

        navigator.clipboard.writeText(copyText);

        Swal.fire({
            title: "Copied!",
            text: "Cases data copied to clipboard.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
            position: "top",
            width: "280px",
        });
    };

    const handlePrint = () => {
        const printContent = `
    <html>
    <head>
        <title>Cases List</title>
        <style>
            body { font-family: Arial; margin: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }
            th { background: #f8f9fa; }
        </style>
    </head>
    <body>
        <h3>Cases List</h3>
        <table>
            <thead>
                <tr>
                    <th>#</th>
                    <th>Case ID</th>
                    <th>Patient</th>
                    <th>Phone</th>
                    <th>Program</th>
                    <th>Created By</th>
                    <th>Date</th>
                    <th>Status</th>
                </tr>
            </thead>
            <tbody>
                ${filteredRecords
                .map(
                    (r, i) => `
                    <tr>
                        <td>${i + 1}</td>
                        <td>${r.case_number}</td>
                        <td>${r.patient_name}</td>
                        <td>${r.patient_phone || ""}</td>
                        <td>${r.program_name || ""}</td>
                        <td>${r.created_by || ""}</td>
                        <td>${r.created_at}</td>
                        <td>${r.status?.name || ""}</td>
                    </tr>`
                )
                .join("")}
            </tbody>
        </table>
    </body>
    </html>`;

        const printWindow = window.open("", "_blank");
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.print();
    };


    /* --------------------------------------------------------------------- */
    /* --------------------------- MODAL HANDLERS -------------------------- */
    /* --------------------------------------------------------------------- */
    const openApproval = (c) => {
        setSelectedCase(c);
        setApprovalModal(true);
        setActiveSlide(0);
        setPhaseData(
            Array(7)
                .fill(null)
                .map(() => ({ remark: "", file: null, fileName: "" }))
        );
    };

    const openHistory = async (c) => {
        setSelectedCase(c);
        setHistoryModal(true);
        setActiveAttachmentsRow(null);
        // Fetch attachments (or phase history)
        await dispatch(fetchCaseHistory(c.id));
    };

    /* --------------------------------------------------------------------- */
    /* --------------------------- PHASE LOGIC ---------------------------- */
    /* --------------------------------------------------------------------- */
    const handleApprove = async () => {
        const current = phaseData[activeSlide];
        if (!current.remark.trim()) {
            Swal.fire("Required", "Please enter remarks.", "warning");
            return;
        }

        const formData = new FormData();
        formData.append("case_id", selectedCase.id);
        formData.append("action", "approve");
        formData.append("remarks", current.remark);

        if (current.file) {
            formData.append("attachments[]", current.file);
        }

        try {
            await dispatch(approveOrRejectCase(formData)).unwrap();

            // ✅ Close modal first
            setApprovalModal(false);

            // Extract current phase for alert
            const statusName = selectedCase?.status?.name || "";
            let currentPhase =
                Number(statusName.match(/Phase\s*(\d+)/i)?.[1]) || activeSlide + 1;
            if (statusName.toLowerCase().includes("completed")) currentPhase = 7;
            if (statusName.toLowerCase().includes("rejected")) currentPhase = activeSlide + 1;

            // ✅ Show SweetAlert after modal is closed
            setTimeout(() => {
                Swal.fire(
                    "Approved",
                    `Phase ${currentPhase} approved.`,
                    "success"
                );
            }, 100); // slight delay ensures modal is fully closed

            // Refresh case history and cases list
            await dispatch(fetchCaseHistory(selectedCase.id));
            await dispatch(fetchCases());

            if (activeSlide < 6) setActiveSlide(activeSlide + 1);

        } catch (err) {
            Swal.fire("Error", err || "Failed to approve phase", "error");
        }
    };


    // const handleReject = async () => {
    //     const current = phaseData[activeSlide];
    //     const formData = new FormData();
    //     formData.append("case_id", selectedCase.id);
    //     formData.append("action", "reject");
    //     formData.append("remarks", current?.remark || "Rejected");

    //     if (current.file) {
    //         formData.append("attachments[]", current.file);
    //     }

    //     try {
    //         await dispatch(approveOrRejectCase(formData)).unwrap();

    //         // ✅ Close modal first
    //         setApprovalModal(false);

    //         const statusName = selectedCase?.status?.name || "";
    //         let currentPhase =
    //             Number(statusName.match(/Phase\s*(\d+)/i)?.[1]) || activeSlide + 1;
    //         if (statusName.toLowerCase().includes("completed")) currentPhase = 7;

    //         // ✅ Show SweetAlert after modal closes
    //         setTimeout(() => {
    //             Swal.fire(
    //                 "Rejected",
    //                 `Phase ${currentPhase} rejected.`,
    //                 "error"
    //             );
    //         }, 100);

    //         // Refresh case history and cases list
    //         await dispatch(fetchCaseHistory(selectedCase.id));
    //         await dispatch(fetchCases());

    //     } catch (err) {
    //         Swal.fire("Error", err || "Failed to reject phase", "error");
    //     }
    // };

    const handleSendBack = async () => {
        const current = phaseData[activeSlide];
        const formData = new FormData();
        formData.append("case_id", selectedCase.id);
        formData.append("action", "send_back"); // updated action
        formData.append("remarks", current?.remark || "Sent Back");

        if (current.file) {
            formData.append("attachments[]", current.file);
        }

        try {
            await dispatch(approveOrRejectCase(formData)).unwrap();

            // Close modal first
            setApprovalModal(false);

            // Extract current phase for alert
            const statusName = selectedCase?.status?.name || "";
            let currentPhase =
                Number(statusName.match(/Phase\s*(\d+)/i)?.[1]) || activeSlide + 1;
            if (statusName.toLowerCase().includes("completed")) currentPhase = 7;

            // Show SweetAlert after modal closes
            setTimeout(() => {
                Swal.fire(
                    "Sent Back",
                    `Phase ${currentPhase} sent back successfully.`,
                    "info"
                );
            }, 100);

            // Refresh case history and cases list
            await dispatch(fetchCaseHistory(selectedCase.id));
            await dispatch(fetchCases());

        } catch (err) {
            Swal.fire("Error", err || "Failed to send back phase", "error");
        }
    };

    const updatePhaseData = (field, value) => {
        setPhaseData((prev) => {
            const updated = [...prev];
            updated[activeSlide] = { ...updated[activeSlide], [field]: value };
            return updated;
        });
    };

    /* --------------------------------------------------------------------- */
    /* --------------------------- HISTORY EXPORT -------------------------- */
    /* --------------------------------------------------------------------- */
    const exportHistoryPDF = () => {
        if (!caseHistory || caseHistory.length === 0) {
            Swal.fire("No Data", "No history available to export.", "info");
            return;
        }

        const doc = new jsPDF();
        doc.text(`History - ${selectedCase?.case_code}`, 14, 10);

        const tableColumn = ["File Name", "Uploaded At"];
        const tableRows = caseHistory
            .flatMap((h) => h.attachments || [])
            .map((a) => [a.file_name, a.created_at || "-"]);

        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
        doc.save(`history_${selectedCase?.case_code}.pdf`);
    };

    /* --------------------------------------------------------------------- */
    /* ------------------------------ TABLE COLUMNS ------------------------ */
    /* --------------------------------------------------------------------- */
    const columns = [
        {
            name: "S.No",
            cell: (_, i) => start + i,
            width: "60px",
        },
        {
            name: "Case ID", selector: (r) => r.case_number, sortable: true,
            width: "100px"
        },
        {
            name: "Patient Name", selector: (r) => r.patient_name, sortable: true,
            width: "150px"
        },
        {
            name: "Phone", selector: (r) => r.patient?.phone || r.patient_number || "N/A",
            width: "130px"
        },
        {
            name: "Program", selector: (r) => r.program.name || "N/A",
            width: "200px"
        },
        { name: "Created By", selector: (r) => r.creator?.name || "N/A" },
        { name: "Created At", selector: (r) => r.created_at.split("T")[0] },
        {
            name: "Status",
            cell: (row) => {
                let badgeClass = "bg-secondary"; // default color

                if (row.status?.name?.includes("Phase")) {
                    badgeClass = "bg-warning";
                } else if (row.status?.name === "Completed") {
                    badgeClass = "bg-success";
                } else if (row.status?.name === "Rejected") {
                    badgeClass = "bg-danger";
                }

                return (
                    <span className={`badge ${badgeClass}`}>
                        {row.status?.name || "N/A"}
                    </span>
                );
            },
        },
        {
            name: "Actions",
            cell: (row) => {
                const patientId = row.patient?.id || row.patient_id;

                return (
                    <div className="d-flex gap-1">
                        {canManageProfile && (
                            <button
                                className="btn btn-sm btn-outline-success"
                                title="Case Profile"
                                onClick={() => navigate(`/cases/${row.id}/profile`, { state: { caseId: row.id, patientId } })}
                            >
                                <i className="ri-profile-line"></i>
                            </button>
                        )}
                        {/* {canManageApproval && (
                            <button
                                className="btn btn-sm btn-outline-primary"
                                title="Manage Approval"
                                onClick={() => openApproval(row)}
                            >
                                <i className="ri-settings-line"></i>
                            </button>
                        )}
                        {canSeeCaseHistory && (
                            <button
                                className="btn btn-sm btn-outline-info"
                                title="View History"
                                onClick={() => openHistory(row)}
                            >
                                <i className="ri-history-line"></i>
                            </button>
                        )} */}
                    </div>
                );
            },
            ignoreRowClick: true,
            width: "150px",
        },
    ];

    /* --------------------------------------------------------------------- */
    /* --------------------------- RENDER SECTION -------------------------- */
    /* --------------------------------------------------------------------- */
    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                {/* -------------------------- TABLE CARD -------------------------- */}
                <div className="card">
                    <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2 p-3">
                        <div className="d-flex align-items-center gap-2">
                            <div className="dropdown">
                                <button
                                    className="btn btn-label-primary btn-sm dropdown-toggle"
                                    data-bs-toggle="dropdown"
                                >
                                    <i className="ri-download-2-line me-1"></i> Export
                                </button>
                                <ul className="dropdown-menu shadow-sm">
                                    <li>
                                        <button className="dropdown-item" onClick={handleExportPDF}>
                                            <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                        </button>
                                    </li>

                                    <li>
                                        <CSVLink
                                            data={filteredRecords}
                                            filename="cases_export.csv"
                                            className="dropdown-item"
                                        >
                                            <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                        </CSVLink>
                                    </li>

                                    <li>
                                        <button className="dropdown-item" onClick={handleCopy}>
                                            <i className="ri-file-copy-line me-1"></i> Copy
                                        </button>
                                    </li>

                                    <li>
                                        <button className="dropdown-item" onClick={handlePrint}>
                                            <i className="ri-printer-line me-1"></i> Print
                                        </button>
                                    </li>
                                </ul>

                            </div>
                            <input
                                type="text"
                                placeholder="Search cases..."
                                className="form-control form-control-sm"
                                style={{ width: "250px" }}
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                            />
                        </div>
                        <h6 className="mb-0 text-center text-muted flex-grow-1">
                            Showing {start}-{end} of {totalRows} cases
                        </h6>
                    </div>

                    <div className="card-body p-0">
                        <DataTable
                            columns={columns}
                            data={paginatedRecords}
                            progressPending={isLoading}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationComponent={CustomPagination}
                            highlightOnHover
                            striped
                            responsive
                            noDataComponent={
                                <div className="p-4 text-center text-muted">
                                    <i className="ri-file-list-3-line fs-1 d-block mb-2"></i>
                                    No cases found
                                </div>
                            }
                        />
                    </div>
                </div>

                {/* ======================== APPROVAL MODAL ======================== */}
                <Modal isOpen={approvalModal} toggle={() => setApprovalModal(false)} size="lg" centered>
                    <div className="modal-content rounded-4 shadow-lg p-4">
                        <h5 className="text-center fw-semibold mb-4">Case Cycle</h5>
                        <h5 className="text-center">
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block">
                                {selectedCase?.status?.name
                                    ? `${selectedCase.status.name} - Approval`
                                    : `Phase ${activeSlide + 1} – Approval`}
                            </small>
                        </h5>

                        {/* Progress Bar */}
                        <div className="mb-4">
                            {(() => {
                                const totalPhases = 8;
                                const statusName = selectedCase?.status?.name || "";
                                let currentPhase =
                                    Number(statusName.match(/Phase\s*(\d+)/i)?.[1]) || activeSlide + 1;

                                // ✅ Handle special cases
                                if (statusName.toLowerCase().includes("completed")) currentPhase = totalPhases;
                                if (statusName.toLowerCase().includes("rejected")) currentPhase = 0;

                                const progressPercent = (currentPhase / totalPhases) * 100;

                                // ✅ Display label text
                                const currentLabel =
                                    currentPhase === totalPhases
                                        ? "Completed"
                                        : currentPhase === 0
                                            ? "Rejected"
                                            : `Phase ${currentPhase}`;

                                return (
                                    <>
                                        <div className="progress" style={{ height: "6px" }}>
                                            <div
                                                className={`progress-bar ${currentPhase === totalPhases
                                                    ? "bg-success"
                                                    : currentPhase === 0
                                                        ? "bg-danger"
                                                        : "bg-primary"
                                                    }`}
                                                style={{ width: `${progressPercent}%` }}
                                            />
                                        </div>
                                        <div className="d-flex justify-content-between mt-2 text-muted small">
                                            <span>{currentLabel}</span>
                                            <span>Completed</span>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>


                        {/* Form */}
                        <div className="row g-3 mb-4">
                            <div className="col-12">
                                <div className="form-floating form-floating-outline">
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        placeholder="Enter remarks"
                                        value={phaseData[activeSlide]?.remark || ""}
                                        onChange={(e) => updatePhaseData("remark", e.target.value)}
                                        style={{ height: "100px" }}
                                    />
                                    <label>Remarks</label>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="form-floating form-floating-outline">
                                    <input
                                        type="file"
                                        className="form-control"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                updatePhaseData("file", file);
                                                updatePhaseData("fileName", file.name);
                                            }
                                        }}
                                    />
                                    <label>
                                        {phaseData[activeSlide]?.fileName || "Upload Document"}
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Approve / Reject */}
                        <div className="row justify-content-center g-3">
                            <div className="col-md-2 col-6">
                                <button
                                    className="btn btn-sm btn-outline-success w-100"
                                    onClick={handleApprove}
                                >
                                    Approve
                                </button>
                            </div>
                            <div className="col-md-2 col-6">
                                <button
                                    className="btn btn-sm btn-outline-danger w-100"
                                    onClick={handleSendBack} // updated handler
                                >
                                    Send Back
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal>

                {/* ======================== HISTORY MODAL ======================== */}
                <Modal isOpen={historyModal} toggle={() => setHistoryModal(false)} size="lg" centered>
                    <h5 className="text-center fw-semibold my-4">
                        Case History - {selectedCase?.case_code || "Case ID"}
                    </h5>

                    <ModalBody className="p-2">
                        {/* History Table */}
                        <div style={{ maxHeight: "300px", overflowY: "auto", overflowX: "auto" }}>
                            <DataTable
                                columns={[
                                    {
                                        name: "Status",
                                        cell: (row) => {
                                            let statusName = row.status_master_name || "Pending"; // use Phase value here
                                            const labelClass =
                                                statusName.includes("Phase") // Phase 1-7 as warning
                                                    ? "bg-label-warning"
                                                    : statusName === "Completed" // Completed as success
                                                        ? "bg-label-success"
                                                        : "bg-label-secondary"; // fallback for any other status

                                            return <span className={`badge rounded-pill ${labelClass}`}>{statusName}</span>;
                                        },
                                        sortable: true,
                                        width: "100px",
                                    },
                                    { name: "Remarks", selector: (r) => r.remarks || "-", sortable: true, width: "200px" },
                                    { name: "Created By", selector: (r) => r.created_by_name || "-", sortable: true, width: "120px" },
                                    { name: "Updated By", selector: (r) => r.updated_by_name || "-", sortable: true, width: "120px" },
                                    { name: "Date Time", selector: (r) => r.datetime?.split("T")[0] || "-", sortable: true, width: "150px" },
                                    {
                                        name: "Attachments",
                                        cell: (row) =>
                                            row.attachments && row.attachments.length > 0 ? (
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() =>
                                                        setActiveAttachmentsRow(activeAttachmentsRow === row.id ? null : row.id)
                                                    }
                                                    title="View Attachments"
                                                >
                                                    <i className="ri-attachment-line"></i>
                                                </button>
                                            ) : null,
                                        width: "150px",
                                    },
                                ]}
                                data={caseHistory} // use Redux state for case history
                                highlightOnHover
                                striped
                                noDataComponent={<div className="text-center py-4 text-muted">No history available</div>}
                            />
                        </div>

                        {/* Attachments Section */}
                        {activeAttachmentsRow && (
                            <div className="mt-3">
                                <h6 className="fw-semibold">Attachments</h6>
                                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                                    <DataTable
                                        columns={[
                                            {
                                                name: "Preview",
                                                cell: (row) => {
                                                    const isImage = row.file_name.match(/\.(jpg|jpeg|png|gif)$/i);
                                                    return isImage ? (
                                                        <img
                                                            src={row.file_path}
                                                            alt={row.file_name}
                                                            style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "4px" }}
                                                        />
                                                    ) : (
                                                        <i className="ri-file-line fs-4 text-muted"></i>
                                                    );
                                                },
                                                width: "70px",
                                            },
                                            { name: "File Name", selector: (r) => r.file_name },
                                            { name: "Uploaded At", selector: (r) => r.created_at || "-" },
                                            {
                                                name: "Action",
                                                cell: (row) => (
                                                    <a
                                                        href={row.file_path}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-outline-primary"
                                                        title="View Attachment"
                                                    >
                                                        <i className="ri-attachment-line"></i>
                                                    </a>
                                                ),
                                                width: "90px",
                                            },
                                        ]}
                                        data={caseHistory.find((h) => h.id === activeAttachmentsRow)?.attachments || []}
                                        highlightOnHover
                                        striped
                                        noDataComponent={<div className="text-center py-4 text-muted">No attachments</div>}
                                    />
                                </div>
                            </div>
                        )}
                    </ModalBody>
                </Modal>
            </div>
        </MainLayout>
    );
};

export default CasesList;